import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import glamorous from 'glamorous';
import gql from 'graphql-tag';
import Section from './section';
import StackCard from '../../../../shared/library/cards/stack/stack-card';
import {MobileContext} from '../../../../shared/enhancers/mobile-enhancer';
import AddItem from '../../../../shared/library/buttons/add-item/index.jsx';
import {OwnerContext} from '../../enhancers/company-profile';
import FreemiumMessage from '../../../../shared/library/freemium-message';
import GithubInstallMessage from '../../../../shared/library/github-install-message';
import SearchView from '../../../../shared/library/search/search';
import {CurrentUserContext} from '../../../../shared/enhancers/current-user-enhancer';
import {IsAdminContext} from '../../enhancers/is-admin';

const Container = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

export const query = gql`
  query stacksList($id: ID!, $orgId: ID, $first: Int, $after: String, $queryVar: String) {
    company(id: $id) {
      id
      stacksList(orgId: $orgId, first: $first, after: $after, query: $queryVar) {
        count
        edges {
          node {
            websiteUrl
            id
            slug
            name
            path
            private
            repoStack
            services(withoutPackages: true) {
              id
              name
              imageUrl
              canonicalUrl
              layer {
                id
                slug
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const TechStacks = ({
  count,
  companyId,
  companySlug,
  companyGithubAppSettingsUrl,
  githubAppInstalled,
  azureAppInstalled,
  privateCompany,
  currentOrg
}) => {
  const ownerContext = useContext(OwnerContext);
  const currentUser = useContext(CurrentUserContext);
  const isMobile = useContext(MobileContext);
  const isOwner = ownerContext.currentUserIsOwner || (currentUser && currentUser.amIAdmin);
  const isAdmin = useContext(IsAdminContext);
  return (
    <Section
      title="Tech Stacks"
      count={count}
      query={query}
      dataKey="stacksList"
      variables={{
        id: companyId,
        queryVar: ownerContext.searchText,
        orgId: currentOrg && currentOrg.id
      }}
      gridCols={isMobile ? 1 : 3}
      initialPageSize={isMobile ? 3 : 9}
      pageSize={9}
      customTitleComponent={
        ownerContext.currentUserIsOwner &&
        isAdmin && (
          <Container>
            <AddItem addItemTitle="Add stack" addItemLink="/create-stack/stack-type" />
          </Container>
        )
      }
      middleDetail={
        ownerContext.currentUserIsOwner && (
          <>
            {(githubAppInstalled || azureAppInstalled) && privateCompany && (
              <FreemiumMessage
                companyId={companyId}
                companySlug={companySlug}
                discardOption={true}
                fileName={'TechStacksCompany'}
                width={765}
                fontSize={13}
              />
            )}
            {githubAppInstalled && privateCompany && (
              <GithubInstallMessage
                companyId={companyId}
                discardOption={true}
                fileName={'StacksReportDashboard'}
                width={765}
                githubAppSettingsUrl={companyGithubAppSettingsUrl}
              />
            )}
            {count > 9 && <SearchView placeholder={'Search Stacks'} context={OwnerContext} />}
          </>
        )
      }
    >
      {({edges}) => {
        return edges.length > 0
          ? edges.map(e => (
              <StackCard
                key={e.node.id}
                name={e.node.name}
                websiteUrl={e.node.websiteUrl}
                count={e.node.services.length}
                services={e.node.services}
                path={e.node.path}
                isPrivate={e.node.private}
                repoStack={e.node.repoStack}
                isOwner={isOwner}
              />
            ))
          : 'No Stacks Found!';
      }}
    </Section>
  );
};

TechStacks.propTypes = {
  count: PropTypes.number.isRequired,
  companyId: PropTypes.any.isRequired,
  companySlug: PropTypes.string.isRequired,
  companyGithubAppSettingsUrl: PropTypes.string.isRequired,
  githubAppInstalled: PropTypes.bool.isRequired,
  azureAppInstalled: PropTypes.bool.isRequired,
  privateCompany: PropTypes.bool.isRequired,
  currentOrg: PropTypes.object
};

export default TechStacks;
